import { Box, Button, Tooltip } from '@mui/material';
import React, { ReactElement, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { useSitesWithGoods } from '../../../hooks/useSitesWithGoods';
import { RootState } from '../../../redux/reducers';
import AddIcon from '@mui/icons-material/Add';
import OrderFormGoodsElement from './OrderFormGoodsElement';
import { createNewEmptyBareGoodsEntity, setBareGoodsEntities } from '../../../redux/actions/orderActions';
import { convertOrderGoodsItemsToBareGoodsEntities } from './OrderFormHelper';
import { filterUniqueBareGoodsByArticle } from '../../../redux/actions/helper';
import { CompleteOrderType } from '../../../common/Types';
import { importArticles } from '../../../redux/actions/articleActions';
import { importApprovedOrderGoodsItems } from '../../../redux/actions/orderGoodsItemsActions';

type OrderFormGoodsProps = {
    completeOrderType?: CompleteOrderType;
    isOriginChosen: boolean;
    isReceipt: boolean;
};

export default function OrderFormGoods(props: OrderFormGoodsProps): ReactElement {
    const { completeOrderType, isOriginChosen, isReceipt } = props;

    const order = useAppSelector((state: RootState) => state.orderForm.order);
    const demandOrder = useAppSelector((state: RootState) => state.demandOrderForm.demandOrder);
    const returnOrder = useAppSelector((state: RootState) => state.returnOrderForm.returnOrder);
    const orderedGoods = useAppSelector((state: RootState) => state.goodsToOrderForm.bareGoodsEntities);
    const sites = useSitesWithGoods();

    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);

    const allArticles = useAppSelector((state: RootState) => state.articles.articles);

    // if orderId exists, get all goods for order and set them as existing goods
    useEffect(() => {
        if (!!order?.id && !!order.orderGoodsItems && order.orderGoodsItems.length > 0 && !!approvedOrderGoodsItems) {
            const site = sites?.find((s) => s.id === order.originSiteId);

            const orderedOrderGoodsItems = approvedOrderGoodsItems.filter(
                (ogi) => !!ogi.order && ogi.order.originSiteId === order.originSiteId && ogi.orderId !== order.id,
            );

            const existingBareGoods = convertOrderGoodsItemsToBareGoodsEntities(
                order.orderGoodsItems,
                site,
                orderedOrderGoodsItems,
            );

            const filteredGoods = filterUniqueBareGoodsByArticle(existingBareGoods);

            dispatch(setBareGoodsEntities(filteredGoods));
        }
    }, [order, approvedOrderGoodsItems]);

    useEffect(() => {
        if (!!demandOrder?.id && !!demandOrder.orderGoodsItems && demandOrder.orderGoodsItems.length > 0) {
            const existingBareGoods = convertOrderGoodsItemsToBareGoodsEntities(demandOrder.orderGoodsItems, undefined);

            const filteredGoods = filterUniqueBareGoodsByArticle(existingBareGoods);

            dispatch(setBareGoodsEntities(filteredGoods));
        }
    }, [demandOrder]);

    useEffect(() => {
        if (!!returnOrder?.id && !!returnOrder.orderGoodsItems && returnOrder.orderGoodsItems.length > 0) {
            const site = sites?.find((s) => s.id === returnOrder.originSiteId);

            const orderedOrderGoodsItems = approvedOrderGoodsItems?.filter(
                (ogi) => !!ogi.order && ogi.order.originSiteId === returnOrder.originSiteId,
            );
            const existingBareGoods = convertOrderGoodsItemsToBareGoodsEntities(
                returnOrder.orderGoodsItems,
                site,
                orderedOrderGoodsItems,
            );

            const filteredGoods = filterUniqueBareGoodsByArticle(existingBareGoods);

            dispatch(setBareGoodsEntities(filteredGoods));
        }
    }, [returnOrder, approvedOrderGoodsItems]);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!allArticles) {
            dispatch(importArticles());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch]);

    const addNewBareGood = () => {
        dispatch(createNewEmptyBareGoodsEntity());
    };

    return (
        <Box
            sx={{
                overflow: 'auto',
                py: 2,
            }}
        >
            <Box>
                {orderedGoods?.map((bareGoods) => {
                    return (
                        <Box key={bareGoods.index} sx={{ mb: 2 }}>
                            <OrderFormGoodsElement
                                orderedBareGoods={bareGoods}
                                completeOrderType={completeOrderType}
                                createNewBareGoods={addNewBareGood}
                            />
                        </Box>
                    );
                })}
                {!isReceipt && (
                    <Tooltip title={isOriginChosen ? 'Ubaci novi artikal' : 'Izaberi izvor - skladište'}>
                        <div>
                            <Button
                                key={'addNewArticle'}
                                variant="outlined"
                                color="primary"
                                sx={{
                                    width: '100%',
                                    height: 60,
                                }}
                                onClick={addNewBareGood}
                                disabled={!isOriginChosen}
                            >
                                <AddIcon sx={{ m: 'auto' }} />
                            </Button>
                        </div>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}
